import type { AnalyticsContext, AnalyticsUser } from './analytics.types';
import { Permission } from './analytics.types';
import { PERMISSION_TO_USER_TYPE, PERMISSION_PRIORITY } from './analytics.constants';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

// Stores the app name set during initAnalytics — included in every event push
// so GTM always reads the correct app_name regardless of dataLayer init order.
let _appName: string | undefined;

// Stores the SHA-256 tenant hash once resolved, so other libs (e.g. authenticatedFetch)
// can read the same value already pushed to GA4 as tenant_id, without re-hashing.
let _tenantIdHash: string | undefined;

const getPrefix = () => `[Analytics${_appName ? `:${_appName}` : ''}]`;

/**
 * SHA-256 hashes a string using the browser's native SubtleCrypto API.
 * Returns a lowercase hex digest.
 */
export async function hashSHA256(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function ensureDataLayer(): void {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
}

export function resolveUserType(permissions: Permission[]): string | null {
  if (permissions.length === 0) {
    return null;
  }

  if (permissions.includes(Permission.ADMIN)) {
    return PERMISSION_TO_USER_TYPE[Permission.ADMIN];
  }

  const primary = [...PERMISSION_PRIORITY].reverse().find((p) => permissions.includes(p));
  return primary ? (PERMISSION_TO_USER_TYPE[primary] ?? null) : null;
}

/**
 * Initializes analytics context on `window.dataLayer`. The context payload is
 * pushed synchronously; the tenant_id push happens asynchronously after the
 * SHA-256 digest resolves. The returned promise settles once the tenant push
 * (or its catch-and-log fallback) completes, so tests can `await` it
 * deterministically. Production callers typically fire-and-forget.
 */
export function initAnalytics(context: AnalyticsContext): Promise<void> {
  _appName = context.appName;
  ensureDataLayer();

  // Push context variables to dataLayer.
  // GTM is loaded statically in index.html so these variables are available
  // on the initial "Initialization - All Pages" trigger.
  // This push also covers standalone remote apps (peri-tray, peri-track, peri-theatre)
  // that may not have the full context seeded in their own index.html.
  window.dataLayer.push({
    ga4_measurement_id: context.ga4MeasurementId,
    environment: context.environment,
    app_name: context.appName,
  });

  // Push tenant_id as SHA256 hash of tenant name (async — fires on next microtask)
  const tenantSource = context.tenantName || context.hospital;
  if (!tenantSource) {
    return Promise.resolve();
  }
  return hashSHA256(tenantSource).then((tenantId) => {
    _tenantIdHash = tenantId;
    ensureDataLayer();
    window.dataLayer.push({ tenant_id: tenantId });
  }).catch((err) => {
    console.warn(`${getPrefix()} failed to hash tenant_id`, err);
  });
}

/**
 * Returns the SHA-256 tenant hash resolved by `initAnalytics`, or `null` if it
 * hasn't resolved yet (e.g. a request fires before the hash promise settles).
 */
export function getTenantIdHash(): string | null {
  return _tenantIdHash ?? null;
}

/**
 * Sets the analytics user properties on `window.dataLayer`. The push happens
 * asynchronously after the SHA-256 digest resolves; the returned promise
 * settles once the dataLayer push (or the catch-and-log fallback) completes,
 * so tests can `await` it deterministically. Production callers typically
 * fire-and-forget — ignoring the returned promise is fine.
 */
export function setUserProperties(user: AnalyticsUser | null): Promise<void> {
  ensureDataLayer();

  if (user === null) {
    const payload = { event: 'user_properties_set', user_id: null, user_role: null, app_session_id: null };
    window.dataLayer.push(payload);
    return Promise.resolve();
  }

  const userRole = resolveUserType(user.permissions);
  // SHA256-hash the email (sourced from Zitadel username) and set as GA4 user_id.
  // GA4 user_id supports up to 256 chars
  // Falls back to user.id if no email is present.
  const emailSource = user.email ?? user.id;
  return hashSHA256(emailSource).then((hashedEmail) => {
    ensureDataLayer();
    window.dataLayer.push({
      event: 'user_properties_set',
      user_id: hashedEmail,
      user_role: userRole,
      app_session_id: user.sessionKey ?? user.id,
    });
  }).catch((err) => {
    console.warn(`${getPrefix()} failed to hash user_id`, err);
  });
}

export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  ensureDataLayer();
  window.dataLayer.push({ event: eventName, app_name: _appName, ...params });
}

/**
 * Returns the GA4 client ID (pseudo-ID) from the _ga cookie.
 * Format in cookie: GA1.1.<random>.<timestamp>  → client_id = "<random>.<timestamp>"
 * Returns null if GTM/GA4 hasn't set the cookie yet.
 */
export function getGaClientId(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/_ga=([^;]+)/);
  if (!match) return null;
  const parts = decodeURIComponent(match[1]).split('.');
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
}

export function trackPageView(
  path: string,
  title?: string,
  dimensions?: Record<string, unknown>
): void {
  ensureDataLayer();
  window.dataLayer.push({
    event: 'page_view',
    app_name: _appName,
    page_path: path,
    page_title: title ?? document.title,
    ...dimensions,
  });
}
